import { customElement, resolve, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import template from './incoming-view.html';
import type { Document } from '../services/api.types';
import { listIncomingDocuments, deleteDocument } from '../services/api.service';
import { ConfirmDialog } from '../components/confirm-dialog';
import { onSseEvent } from '../services/sse.service';

/** EventAggregator channel used to push the authoritative incoming-document count
 *  (as known by this view right after it fetches or mutates the list) up to the
 *  sidebar badge. This keeps the badge accurate even if the corresponding
 *  SSE "incoming_count_changed" announcement was missed by the browser. */
export const INCOMING_COUNT_CHANNEL = 'incoming-count-updated';

@route({ path: 'incoming', title: 'Incoming' })
@customElement({ name: 'incoming-view', template })
export class IncomingView {
  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);

  public documents: Document[] = [];
  public totalCount = 0;
  public page = 1;
  public pageSize = 25;
  public isFetching = false;
  public isLoadingMore = false;
  public fetchError = '';
  public editDocument: Document | null = null;

  // Row selection
  public selectedIds: Set<string> = new Set();
  private _lastSelectedIdx = -1;

  // Touch handling
  private _touchTimer: ReturnType<typeof setTimeout> | null = null;
  private _touchMoved = false;
  private _tapTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastTapDocId = '';
  private _lastTapTime = 0;

  // Infinite scroll
  public scrollContainer: HTMLElement | null = null;
  public scrollSentinel: HTMLElement | null = null;
  private _scrollObserver: IntersectionObserver | null = null;

  private _sseUnsub?: () => void;
  private _refreshTimer?: ReturnType<typeof setTimeout>;

  public get hasMore(): boolean {
    return this.documents.length < this.totalCount;
  }

  public get hasSelection(): boolean { return this.selectedIds.size > 0; }
  public get selectionCount(): number { return this.selectedIds.size; }

  public async loading(): Promise<void> {
    await this._fetchDocuments();

    this._sseUnsub = onSseEvent('incoming_count_changed', () => {
      this._scheduleRefresh();
    });
  }

  public attached(): void {
    if (this.scrollSentinel && this.scrollContainer) {
      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) void this._loadMore();
        },
        { root: this.scrollContainer, threshold: 0.1 },
      );
      this._scrollObserver.observe(this.scrollSentinel);
    }
  }

  public unloading(): void {
    this._scrollObserver?.disconnect();
    this._scrollObserver = null;
    this._sseUnsub?.();
    this._sseUnsub = undefined;
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = undefined;
    }
  }

  private _scheduleRefresh(): void {
    if (this._refreshTimer) clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => {
      this._refreshTimer = undefined;
      if (!this.editDocument) {
        void this._fetchDocuments();
      }
    }, 1500);
  }

  private async _fetchDocuments(): Promise<void> {
    this.isFetching = true;
    this.fetchError = '';
    this.page = 1;
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
    try {
      const result = await listIncomingDocuments(1, this.pageSize);
      this.documents = result.documents;
      this.totalCount = result.totalCount;
      this.ea.publish(INCOMING_COUNT_CHANNEL, this.totalCount);
    } catch (e) {
      this.fetchError = e instanceof Error ? e.message : this.i18n.tr('incoming.load_failed');
    } finally {
      this.isFetching = false;
    }
  }

  private async _loadMore(): Promise<void> {
    if (this.isFetching || this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.page++;
    try {
      const result = await listIncomingDocuments(this.page, this.pageSize);
      this.documents = [...this.documents, ...result.documents];
      this.totalCount = result.totalCount;
    } catch {
      this.page--;
    } finally {
      this.isLoadingMore = false;
    }
  }

  // ── Row selection ──────────────────────────────────────────────────────────

  public onRowClick(doc: Document, index: number, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey) {
      const next = new Set(this.selectedIds);
      if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id);
      this.selectedIds = next;
      this._lastSelectedIdx = index;
    } else if (event.shiftKey && this._lastSelectedIdx >= 0) {
      const start = Math.min(this._lastSelectedIdx, index);
      const end = Math.max(this._lastSelectedIdx, index);
      const next = new Set(this.selectedIds);
      for (let i = start; i <= end; i++) next.add(this.documents[i].id);
      this.selectedIds = next;
    } else {
      this.selectedIds = new Set([doc.id]);
      this._lastSelectedIdx = index;
    }
  }

  public onRowDblClick(doc: Document): boolean {
    this.openEdit(doc);
    return false;
  }

  public onTouchStart(doc: Document): void {
    this._touchMoved = false;
    this._touchTimer = setTimeout(() => {
      this._touchTimer = null;
      this.openEdit(doc);
    }, 600);
  }

  public onTouchMove(): void {
    this._touchMoved = true;
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
  }

  public onTouchEnd(doc: Document, index: number, event: TouchEvent): void {
    if (this._touchTimer !== null) {
      clearTimeout(this._touchTimer);
      this._touchTimer = null;
    }
    if (this._touchMoved) return;
    event.preventDefault();

    const now = Date.now();
    const isDoubleTap = this._lastTapDocId === doc.id && (now - this._lastTapTime) < 350;

    if (isDoubleTap) {
      if (this._tapTimer !== null) { clearTimeout(this._tapTimer); this._tapTimer = null; }
      this._lastTapDocId = '';
      this._lastTapTime = 0;
      this.openEdit(doc);
    } else {
      this._lastTapDocId = doc.id;
      this._lastTapTime = now;
      if (this._tapTimer !== null) clearTimeout(this._tapTimer);
      this._tapTimer = setTimeout(() => {
        this._tapTimer = null;
        const next = new Set(this.selectedIds);
        if (next.has(doc.id)) next.delete(doc.id); else next.add(doc.id);
        this.selectedIds = next;
        this._lastSelectedIdx = index;
      }, 350);
    }
  }

  public clearSelection(): void {
    this.selectedIds = new Set();
    this._lastSelectedIdx = -1;
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public async deleteSelected(): Promise<void> {
    if (this.selectedIds.size === 0) return;

    let message: string;
    if (this.selectedIds.size === 1) {
      const doc = this.documents.find(d => this.selectedIds.has(d.id));
      message = this.i18n.tr('documents.delete.confirmOne', { title: doc?.title ?? '' });
    } else {
      message = this.i18n.tr('documents.delete.confirmMany', { count: this.selectedIds.size });
    }

    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('documents.delete.title'));
    if (!confirmed) return;
    try {
      for (const id of this.selectedIds) {
        await deleteDocument(id);
      }
      this.selectedIds = new Set();
      await this._fetchDocuments();
    } catch (err: unknown) {
      await ConfirmDialog.alert(
        err instanceof Error ? err.message : this.i18n.tr('documents.delete.failed'),
        this.i18n.tr('common.error'),
      );
    }
  }

  // ── Edit / clear ───────────────────────────────────────────────────────────

  public openEdit(doc: Document): void {
    this.editDocument = { ...doc };
  }

  public onDocumentSaved(): void {
    this.editDocument = null;
    void this._fetchDocuments();
  }

  public onDocumentDeleted(): void {
    this.editDocument = null;
    void this._fetchDocuments();
  }

  public onModalClosed(): void {
    this.editDocument = null;
  }

  public async refresh(): Promise<void> {
    await this._fetchDocuments();
  }
}

